/**
 * Chest of Drawers generator — a freestanding dresser built from box geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width  (left–right)
 *   - Y = depth  (front–back; front face at +Y)
 *   - Z = height (floor to top)
 *
 * Local anchor: origin at the geometric centre of the footprint at Z = 0.
 *
 * Default dimensions approximate a standard four-drawer dresser:
 *   0.8 m wide × 0.45 m deep × 0.9 m tall with 4 equal rows.
 *
 * Structure:
 *   - Two full-height side panels
 *   - Top and bottom panels (between sides)
 *   - N − 1 horizontal shelf dividers separating drawer rows
 *   - C − 1 vertical column dividers (full interior height)
 *   - Optional back panel
 *   - Optional toe kick (recessed base panel)
 *   - C × N drawer fronts, each with optional bar, knob, or no hardware
 *
 * Row height modes:
 *   - Uniform: all rows equal height.
 *   - Graduated: lower rows are taller (classical dresser silhouette).
 *     Weight for row i from bottom = (rows − i), giving an arithmetic taper.
 */

import type { PrimitiveGeometry } from "../primitives.ts";
import type { BoxSpec } from "./generatorGeom.ts";
import {
  buildCylinder,
  concatBoxes,
  concatPrimitiveGeometries,
  transformGeometry,
} from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  intParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface ChestOfDrawersParams extends GeneratorParams {
  width: number;
  depth: number;
  height: number;
  panelThickness: number;
  drawerRows: number;
  drawerColumns: number;
  drawerGap: number;
  graduated: boolean;
  pullStyle: string;
  hasToeKick: boolean;
  toeKickHeight: number;
  toeKickInset: number;
  hasBack: boolean;
  backThickness: number;
}

const CHEST_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "chest-dimensions",
    label: "Dimensions",
    params: [
      {
        id: "width",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 0.8,
        min: 0.3,
        max: 2.0,
        step: 0.05,
      },
      {
        id: "depth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.45,
        min: 0.25,
        max: 0.8,
        step: 0.025,
      },
      {
        id: "height",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.9,
        min: 0.4,
        max: 1.8,
        step: 0.05,
      },
      {
        id: "panelThickness",
        label: "Panel Thickness",
        type: "number",
        unit: "meters",
        default: 0.02,
        min: 0.01,
        max: 0.04,
        step: 0.005,
      },
    ],
  },
  {
    type: "section",
    id: "chest-drawers",
    label: "Drawers",
    params: [
      {
        id: "drawerRows",
        label: "Rows",
        type: "number",
        unit: "count",
        default: 4,
        min: 1,
        max: 8,
        step: 1,
      },
      {
        id: "drawerColumns",
        label: "Columns",
        type: "number",
        unit: "count",
        default: 1,
        min: 1,
        max: 4,
        step: 1,
      },
      {
        id: "drawerGap",
        label: "Gap",
        type: "number",
        unit: "meters",
        default: 0.006,
        min: 0.002,
        max: 0.02,
        step: 0.001,
      },
      { id: "graduated", label: "Graduated", type: "boolean", default: false },
    ],
  },
  {
    type: "section",
    id: "chest-hardware",
    label: "Hardware",
    params: [
      {
        id: "pullStyle",
        label: "Pull Style",
        type: "enum",
        default: "bar",
        options: [
          { value: "bar", label: "Bar" },
          { value: "knob", label: "Knob" },
          { value: "none", label: "None" },
        ],
      },
    ],
  },
  {
    type: "section",
    id: "chest-details",
    label: "Details",
    defaultCollapsed: true,
    params: [
      { id: "hasToeKick", label: "Toe Kick", type: "boolean", default: true },
      {
        id: "toeKickHeight",
        label: "Kick Height",
        type: "number",
        unit: "meters",
        default: 0.08,
        min: 0.03,
        max: 0.15,
        step: 0.01,
        showIf: { id: "hasToeKick", value: true },
      },
      {
        id: "toeKickInset",
        label: "Kick Inset",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.01,
        max: 0.1,
        step: 0.005,
        showIf: { id: "hasToeKick", value: true },
      },
      { id: "hasBack", label: "Back Panel", type: "boolean", default: true },
      {
        id: "backThickness",
        label: "Back Thickness",
        type: "number",
        unit: "meters",
        default: 0.008,
        min: 0.004,
        max: 0.02,
        step: 0.002,
        showIf: { id: "hasBack", value: true },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

function buildChestOfDrawers(
  p: ChestOfDrawersParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const width = numParam(p, "width", 0.8);
  const depth = numParam(p, "depth", 0.45);
  const height = numParam(p, "height", 0.9);
  const panelT = numParam(p, "panelThickness", 0.02);
  const rows = Math.max(1, intParam(p, "drawerRows", 4));
  const cols = Math.max(1, intParam(p, "drawerColumns", 1));
  const gap = numParam(p, "drawerGap", 0.006);
  const graduated = boolParam(p, "graduated", false);
  const pullStyle = enumParam(p, "pullStyle", "bar");
  const hasToe = boolParam(p, "hasToeKick", true);
  const toeH = numParam(p, "toeKickHeight", 0.08);
  const toeInset = numParam(p, "toeKickInset", 0.04);
  const hasBack = boolParam(p, "hasBack", true);
  const backT = numParam(p, "backThickness", 0.008);

  const boxes: BoxSpec[] = [];
  const extraGeoms: PrimitiveGeometry[] = [];

  // ── Back offset ────────────────────────────────────────────────────────────
  // Side panels and shelves stop short of the very back so the back panel
  // sits flush against the wall without z-fighting.
  const backOff = hasBack ? backT : 0;

  // ── Inner cavity dimensions ────────────────────────────────────────────────
  const innerW = width - panelT * 2;
  const innerD = depth - backOff;
  const innerDCY = -backOff * 0.5; // Y centre of inner cavity

  // ── Side panels — full height, full depth ─────────────────────────────────
  const sideSY = depth - backOff;
  const sideCY = -backOff * 0.5;
  boxes.push({
    cx: width * 0.5 - panelT * 0.5,
    cy: sideCY,
    cz: height * 0.5,
    sx: panelT,
    sy: sideSY,
    sz: height,
  });
  boxes.push({
    cx: -width * 0.5 + panelT * 0.5,
    cy: sideCY,
    cz: height * 0.5,
    sx: panelT,
    sy: sideSY,
    sz: height,
  });

  // ── Bottom panel ──────────────────────────────────────────────────────────
  const bodyBottomZ = hasToe ? toeH : 0;
  boxes.push({
    cx: 0,
    cy: innerDCY,
    cz: bodyBottomZ + panelT * 0.5,
    sx: innerW,
    sy: innerD,
    sz: panelT,
  });

  // ── Top panel ─────────────────────────────────────────────────────────────
  boxes.push({
    cx: 0,
    cy: innerDCY,
    cz: height - panelT * 0.5,
    sx: innerW,
    sy: innerD,
    sz: panelT,
  });

  // ── Toe kick ──────────────────────────────────────────────────────────────
  // Recessed base panel: narrower than body depth, inset from front.
  if (hasToe) {
    const toeD = depth - backOff - toeInset;
    const toeCY = -(depth * 0.5) + backOff + toeD * 0.5;
    boxes.push({
      cx: 0,
      cy: toeCY,
      cz: toeH * 0.5,
      sx: innerW,
      sy: Math.max(0.01, toeD),
      sz: toeH,
    });
  }

  // ── Back panel ────────────────────────────────────────────────────────────
  if (hasBack) {
    boxes.push({
      cx: 0,
      cy: -(depth - backT) * 0.5,
      cz: height * 0.5,
      sx: width,
      sy: backT,
      sz: height,
    });
  }

  // ── Interior Z extents ────────────────────────────────────────────────────
  const interiorBotZ = bodyBottomZ + panelT;
  const interiorTopZ = height - panelT;
  const interiorH = interiorTopZ - interiorBotZ;

  // Row heights — index 0 = bottom row, index (rows−1) = top row.
  // With N rows there are N−1 horizontal dividers of thickness panelT between them.
  const totalDivH = Math.max(0, rows - 1) * panelT;
  const availableH = Math.max(0.01, interiorH - totalDivH);

  const rowHeights = new Array<number>(rows);
  if (graduated && rows > 1) {
    // Arithmetic taper: weight for row i from bottom = (rows − i).
    // Bottom row (i=0) is tallest; top row (i=rows−1) is shortest.
    const weightSum = (rows * (rows + 1)) / 2;
    for (let i = 0; i < rows; i++) {
      rowHeights[i] = (availableH * (rows - i)) / weightSum;
    }
  } else {
    const uniformH = availableH / rows;
    rowHeights.fill(uniformH);
  }

  // ── Horizontal row dividers ────────────────────────────────────────────────
  {
    let z = interiorBotZ;
    for (let r = 0; r < rows; r++) {
      z += rowHeights[r];
      if (r < rows - 1) {
        boxes.push({
          cx: 0,
          cy: innerDCY,
          cz: z + panelT * 0.5,
          sx: innerW,
          sy: innerD,
          sz: panelT,
        });
        z += panelT;
      }
    }
  }

  // ── Column dimensions ──────────────────────────────────────────────────────
  const totalDivW = Math.max(0, cols - 1) * panelT;
  const colW = Math.max(0.01, (innerW - totalDivW) / cols);

  // ── Vertical column dividers — full interior height ────────────────────────
  for (let c = 1; c < cols; c++) {
    const divCX = -width * 0.5 + panelT + c * (colW + panelT) - panelT * 0.5;
    boxes.push({
      cx: divCX,
      cy: innerDCY,
      cz: interiorBotZ + interiorH * 0.5,
      sx: panelT,
      sy: innerD,
      sz: interiorH,
    });
  }

  // ── Drawer fronts and hardware ─────────────────────────────────────────────
  // Drawer fronts are inset style: front face flush with the case front (+Y face),
  // sized with a visible gap all around the opening.
  const frontT = panelT * 1.2; // slightly thicker than structural panels
  const frontCY = depth * 0.5 - frontT * 0.5; // front face of drawer flush with case front

  {
    let rowBotZ = interiorBotZ;
    for (let r = 0; r < rows; r++) {
      const rh = rowHeights[r];
      const rowCZ = rowBotZ + rh * 0.5;
      const frontH = Math.max(0.01, rh - gap);

      for (let c = 0; c < cols; c++) {
        const colCX = -width * 0.5 + panelT + c * (colW + panelT) + colW * 0.5;
        const frontW = Math.max(0.01, colW - gap);

        // Drawer front panel
        boxes.push({
          cx: colCX,
          cy: frontCY,
          cz: rowCZ,
          sx: frontW,
          sy: frontT,
          sz: frontH,
        });

        // Hardware
        if (pullStyle === "bar") {
          // A horizontal bar handle protruding from the drawer face.
          const barW = Math.min(colW * 0.4, 0.12);
          const barH = 0.012;
          const barD = 0.01; // front-to-back thickness of the bar
          const barCY = depth * 0.5 + barD * 0.5; // back of bar flush with case front
          boxes.push({
            cx: colCX,
            cy: barCY,
            cz: rowCZ,
            sx: barW,
            sy: barD,
            sz: barH,
          });
        } else if (pullStyle === "knob") {
          // A small cylinder oriented along +Y (pointing out from the drawer face).
          // buildCylinder produces a Z-axis cylinder; rotate −90° around X to align with Y.
          const knobR = 0.011;
          const knobH = 0.018;
          const knob = buildCylinder(knobR, knobH, 16);
          // After rotation, cylinder extends from y = −knobH/2 to y = +knobH/2 at origin.
          // Translate so the base sits at the case front face (Y = depth/2).
          const knobCY = depth * 0.5 + knobH * 0.5;
          extraGeoms.push(
            transformGeometry(
              knob,
              [colCX, knobCY, rowCZ],
              [-Math.PI * 0.5, 0, 0],
            ),
          );
        }
      }

      rowBotZ += rh + (r < rows - 1 ? panelT : 0);
    }
  }

  // ── Combine all parts ─────────────────────────────────────────────────────
  const boxGeom = concatBoxes(boxes);
  return {
    kind: "mesh",
    geometry:
      extraGeoms.length === 0
        ? boxGeom
        : concatPrimitiveGeometries([boxGeom, ...extraGeoms]),
  };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const CHEST_OF_DRAWERS_GENERATOR: Generator =
  defineGenerator<ChestOfDrawersParams>({
    id: "chest_of_drawers",
    label: "Chest of Drawers",
    category: "prop",
    paramSpecs: CHEST_SPECS,
    defaultParams: paramsFromSpecs(CHEST_SPECS) as ChestOfDrawersParams,
    build: buildChestOfDrawers,
  });
